/**
 * Fetch Consumer Insurance Actor
 *
 * Fires a background insurance check-and-reprice for a returning patient,
 * then runs a real eligibility check (getInsuranceStatuses) to verify the
 * insurance against the patient's identity. The reprice only validates card
 * data — it doesn't catch DOB/name mismatches.
 *
 * Spawned as a detached child when PATIENT_MATCHED — result is captured via
 * the top-level `xstate.done.actor.fetchConsumerInsurance` event handler in
 * the returning-patient arc.
 */

import { getInsuranceStatuses } from "@frontend/lib/actions/actions";
import { RequestSource } from "@frontend/lib/schemas/clientSchema";
import { fromPromise } from "xstate";
import { z } from "zod";
import { trackNetworkRequest } from "../../lib/network-diagnostics";
import {
  type ConsumerInsurance,
  checkAndRepriceByConsumerId,
} from "../../server-actions/pc-actions";
import type { ActorMetadata, ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema — what this actor needs from machine context.
 */
const FetchConsumerInsuranceInputSchema = z.object({
  consumerId: z.string(),
  orgId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string(),
});

type FetchConsumerInsuranceInput = z.infer<
  typeof FetchConsumerInsuranceInputSchema
>;

/**
 * Success Schema — what this actor returns on success.
 */
const FetchConsumerInsuranceSuccessSchema = z.object({
  primaryInsurance: z.custom<ConsumerInsurance>().nullable(),
  secondaryInsurance: z.custom<ConsumerInsurance>().nullable(),
});

type FetchConsumerInsuranceSuccess = z.infer<
  typeof FetchConsumerInsuranceSuccessSchema
>;

/**
 * Actor Implementation
 */
const fetchConsumerInsuranceActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: FetchConsumerInsuranceInput;
  }): Promise<FetchConsumerInsuranceSuccess> => {
    const validated = FetchConsumerInsuranceInputSchema.safeParse(input);

    if (!validated.success) {
      throw new Error(
        `fetchConsumerInsurance validation failed: ${validated.error.message}`,
      );
    }

    const { consumerId, orgId, firstName, lastName, dateOfBirth } =
      validated.data;

    const tracker = trackNetworkRequest("checkAndRepriceByConsumerId", {
      orgId,
    });

    try {
      // Step 1: Reprice — get insurance card data (payer, memberId, planName, etc.)
      const result = await checkAndRepriceByConsumerId(consumerId);
      tracker.success();

      const insurances = Array.isArray(result) ? result : [];
      const primaryInsurance = insurances.find((i) => i.priority === 1) ?? null;
      const secondaryInsurance =
        insurances.find((i) => i.priority === 2) ?? null;

      if (!primaryInsurance) {
        return { primaryInsurance: null, secondaryInsurance: null };
      }

      // Step 2: Real eligibility check — verify against patient identity
      // Pass consumer name as insured name (kiosk assumes patient = insured)
      const insuranceList = insurances.map((ins) => ({
        memberId: ins.memberId,
        payer: ins.payer,
        state: ins.state,
        insuredFirstName: ins.insuredFirstName ?? firstName,
        insuredLastName: ins.insuredLastName ?? lastName,
      }));

      try {
        const eligibilityResult = await getInsuranceStatuses(
          { fname: firstName, lname: lastName, dob: dateOfBirth },
          insuranceList,
          orgId,
          RequestSource.KIOSK,
          null,
          undefined,
          undefined,
        );

        // Override reprice status/network with real eligibility result
        if (eligibilityResult[0] && primaryInsurance) {
          primaryInsurance.status = eligibilityResult[0].status;
          primaryInsurance.network = eligibilityResult[0].network;
        }
        if (eligibilityResult[1] && secondaryInsurance) {
          secondaryInsurance.status = eligibilityResult[1].status;
          secondaryInsurance.network = eligibilityResult[1].network;
        }
      } catch {
        // Eligibility check failed — fall back to reprice data
        // (better to show potentially stale status than block the flow)
      }

      return { primaryInsurance, secondaryInsurance };
    } catch (error) {
      tracker.error(error);
      throw error;
    }
  },
);

/**
 * Fetch Consumer Insurance Actor with Metadata
 */
export const fetchConsumerInsuranceActor: ActorWithMetadata = {
  impl: fetchConsumerInsuranceActorImpl,
  metadata: {
    displayName: "fetchConsumerInsurance",
    description:
      "Background insurance check-and-reprice with real eligibility verification for a returning patient",
    category: "data",
    retryable: false,
    timeout: 30000, // 30 seconds — reprice + eligibility can be slow
    errorCodes: [],
    inputSchema: FetchConsumerInsuranceInputSchema,
    successSchema: FetchConsumerInsuranceSuccessSchema,
    errorSchema: z.object({}),
    onSuccess: (result: unknown) => {
      const r = result as FetchConsumerInsuranceSuccess;
      return {
        _returningPatientPrimaryInsurance: r.primaryInsurance,
        _returningPatientSecondaryInsurance: r.secondaryInsurance,
      };
    },
    onError: (_error: unknown) => {
      return {};
    },
  } satisfies ActorMetadata,
};
